import { getTotalPrice } from '@shared/lib/money';
import * as React from 'react';
import { SegmentTabs, SegmentTabType } from '@shared/ui/SegmentTabs';
import { useTranslation } from 'react-i18next';
import {
	NASProductCategory,
	NASProductFragment,
	NASStatus,
	PassengerFragment,
	OrderStatus,
	SegmentFragment
} from '@websky/graphql';
import EveryPortCard from '../../../../EveryPortCard/EveryPortCard/EveryPortCard';
import { EveryPortSelectedService } from '../../../../Checkout/store/everyPortServices/reducers';
import { useTheme } from '../../../../theme';
import { format, MOBILE_MIN_WIDTH } from '../../../../utils';
import MediaQuery from 'react-responsive';
import Toolbar from '../../../../Toolbar';
import SimpleLoader from '../../../../SimpleLoader';
import Options from '../Options/Options';
import SelectedService from './SelectedService/SelectedService';
import { SetEveryPortService } from '../../../../Checkout/store/everyPortServices/actions';
import { filterServicesForAdultTravellerExists, findMinPrice, getEveryPortSelectedServices } from '../utils';
import { PassengerType } from '../../../../enums';
import Rules from '../Rules/Rules';
import { useEveryPortState } from './useEveryPortState.hook';
import { useConfig } from '../../../../context';
import { useSelector } from 'react-redux';
import { getOrderStatus } from '../../../../Checkout/store/order/selectors';
import WebskyAnalyticsApollo from '../../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { WxEvents } from '../../../../DataLayerAnalytics/types';

interface Props {
	everyPortServices: NASProductFragment[];
	selectedServices: EveryPortSelectedService[];
	segments: SegmentFragment[];
	onServiceSave: (payload: SetEveryPortService[]) => void;
	onClose?: () => void;
	segmentId?: string;
	loading?: boolean;
	travellers: PassengerFragment[];
	type?: NASProductCategory;
	header: string;
	readonly?: boolean;
}

const Inner: React.FC<Props> = ({
	everyPortServices,
	segments,
	selectedServices,
	onServiceSave,
	onClose,
	segmentId: defaultSegmentId,
	loading,
	travellers,
	type,
	header,
	readonly
}) => {
	const { t } = useTranslation('ServiceAggregation'),
		{ nasServiceUpsaleForConfirmedOrder } = useConfig().Engine,
		orderStatus = useSelector(getOrderStatus);

	const { state, setTraveller, removeTraveller, clearSegment } = useEveryPortState(
		selectedServices,
		everyPortServices
	);

	const [segmentId, setSegmentId] = React.useState(defaultSegmentId ?? segments[0].id);
	const [selectedServiceId, setSelectedServiceId] = React.useState(null);

	const modifyAvailable =
		orderStatus === OrderStatus.New ||
		orderStatus === OrderStatus.Booked ||
		(nasServiceUpsaleForConfirmedOrder && orderStatus !== OrderStatus.Cancelled);

	const selectedService = React.useMemo(() => {
		return everyPortServices.find(service => service.id === selectedServiceId);
	}, [selectedServiceId]);

	const theme = useTheme('CheckoutAdditionalService').EveryPortLounge;

	const onServiceSelect = (service: NASProductFragment) => {
		if (service.id === selectedServiceId) {
			setSelectedServiceId(null);

			clearSegment(service.segmentId.toString());
		} else {
			setSelectedServiceId(service.id);

			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.NASVipItem, {
				segmentId: service.segmentId.toString(),
				servicesIds: [service.id]
			});
		}
	};

	const segmentsMap = React.useMemo(() => {
		return new Map(segments.map(segment => [segment.id, segment]));
	}, [segments]);

	const segment = React.useMemo(() => {
		return segmentsMap.get(segmentId);
	}, [segmentsMap, segmentId]);

	const tabs = React.useMemo(() => {
		const segmentTabs: SegmentTabType[] = [];

		everyPortServices.forEach(service => {
			const serviceSegmentId = service.segmentId.toString();

			if (!segmentTabs.some(segment => segment.id === serviceSegmentId)) {
				const segment = segmentsMap.get(serviceSegmentId);

				if (segment) {
					segmentTabs.push({
						id: segment.id,
						title: segment.departure.airport.city.name,
						isDisabled: false,
						countSeatsSelected: state.reduce((count, item) => {
							if (item.product.segmentId.toString() === segment.id) {
								return count + 1;
							}

							return count;
						}, 0)
					});
				}
			}
		});

		return segmentTabs;
	}, [everyPortServices, segmentsMap, state]);

	const onNextClick = () => {
		onServiceSave(getEveryPortSelectedServices(filterServicesForAdultTravellerExists(state, travellers)));

		onClose();
	};

	const onBackClick = () => {
		onClose();
	};

	const totalPrice = React.useMemo(() => {
		return getTotalPrice(
			state.filter(item => item.fare.prices && item.status === NASStatus.New),
			item => item.fare.prices?.find(price => price.traveller.id === item.travellerId)?.price
		);
	}, [state]);

	React.useEffect(() => {
		if (defaultSegmentId) {
			setSegmentId(defaultSegmentId);
		}
	}, [defaultSegmentId]);

	React.useEffect(() => {
		const selectedServiceInSegment = state.find(item => item.product.segmentId.toString() === segmentId);

		if (selectedServiceInSegment) {
			setSelectedServiceId(selectedServiceInSegment.product.id);
		} else {
			setSelectedServiceId(null);
		}
	}, [segmentId]);

	const selectedTravellersInSegment = React.useMemo(() => {
		return state.filter(item => item.product.segmentId.toString() === segmentId);
	}, [state, segmentId]);

	const isDifferentSelectedServicesCount = React.useMemo(() => {
		const selectedServicesCount = selectedServices.reduce(
			(count, service) => count + service.travellerIds.length,
			0
		);

		return Math.abs(selectedServicesCount - state.length) > 0;
	}, [state, selectedServices]);

	const everyPortServicesInSegment = React.useMemo(() => {
		return everyPortServices.filter(service => service.segmentId.toString() === segmentId);
	}, [everyPortServices, segmentId]);

	const noAdultsInTravellers = React.useMemo(() => {
		const travellersOnSegment = state.filter(item => item.product.segmentId.toString() === segmentId);

		if (travellersOnSegment.length > 0) {
			return travellersOnSegment.every(
				travellerOnSegment =>
					travellers.find(traveller => traveller.id === travellerOnSegment.travellerId).type !==
					PassengerType.Adult
			);
		}

		return false;
	}, [state, travellers, segmentId]);

	React.useEffect(() => {
		if (!selectedServiceId && everyPortServicesInSegment.length === 1) {
			setSelectedServiceId(everyPortServicesInSegment[0].id);
		}
	}, [selectedServiceId, everyPortServicesInSegment]);

	const [rulesOpen, setRulesOpen] = React.useState(false);

	const openRules = () => {
		if (selectedService.terms[0]?.html) {
			setRulesOpen(true);
		} else {
			selectedService.terms.forEach(term => window.open(term.url, '_blank'));
		}
	};

	const closeRules = () => setRulesOpen(false);

	const isNewServicesExists = React.useMemo(() => {
		return state.some(product => product.status === NASStatus.New);
	}, [state]);

	return (
		<>
			{loading && <SimpleLoader />}

			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				<SegmentTabs
					title={header}
					activeSegment={segmentId}
					segments={tabs}
					onSegmentClick={setSegmentId}
					onClose={onClose}
					sticky={true}
				/>
			</MediaQuery>

			<div className={theme.inner}>
				<div className={theme.header}>
					<div>
						{format(segment.departure.date, 'dd MMM yyyy')} — {segment.departure.airport.name}
					</div>
				</div>

				{!!selectedService && (
					<>
						<Options product={selectedService} onRulesOpen={openRules} />

						<Rules rules={selectedService.terms} onClose={closeRules} open={rulesOpen} />
					</>
				)}

				<div className={theme.container}>
					{!!selectedService && (
						<SelectedService
							service={selectedService}
							travellers={travellers}
							onCancel={
								everyPortServicesInSegment?.length > 1 ? () => onServiceSelect(selectedService) : null
							}
							addTraveller={setTraveller}
							removeTraveller={removeTraveller}
							selectedTravellersInSegment={selectedTravellersInSegment}
							modifyAvailable={modifyAvailable}
						/>
					)}

					{!selectedServiceId &&
						everyPortServicesInSegment.map(service => {
							return (
								<EveryPortCard
									key={service.id}
									options={service.facilities}
									price={findMinPrice(service, travellers)}
									images={service.images}
									terminalName={service.terminalName}
									name={service.name}
									description={service.description}
									location={service.meetingPoint}
									onSelect={() => {
										onServiceSelect(service);
									}}
									category={service.category}
								/>
							);
						})}
				</div>
			</div>

			{(isDifferentSelectedServicesCount || (state.length > 0 && isNewServicesExists)) && (
				<Toolbar
					buttonDisabled={noAdultsInTravellers}
					totalPrice={totalPrice}
					onButtonClick={onNextClick}
					nextButtonText={t('Confirm')}
					onBackButtonClick={onBackClick}
				/>
			)}
		</>
	);
};

export default Inner;
